import { useLibraryStates } from "frontend/canvas-designer-new/shape-libraries/side-panel/context/library-context";
import { useMemo } from "react";
import { ShapeLibraries, ShapeLibrary } from "frontend/canvas-designer-new/shape-libraries/types";
import { LibraryRowType, LibraryRow } from "../types";
import { FREE_LIBRARIES } from "frontend/canvas-designer-new/shape-libraries/consts";
import { filterLibrary } from "../../utils";
import { useIsCIMReorderEnabled } from "utils/url-utils";

export const ITEMS_PER_ROW = 6;
export const CIM_ITEMS_PER_ROW = 5;

export const ROW_HEIGHTS: Record<LibraryRowType, number> = {
  "library-header": 40,
  "group-title": 50,
  "first-group-title": 28,
  "items-row": 42,
  "preview-footer": 20,
  error: 125,
};

function flattenLibrary(
  library: ShapeLibrary,
  isExpanded: boolean,
  isError: boolean,
  isPreview: boolean,
  itemsInLibrary: number,
  isCIM: boolean
): LibraryRow[] {
  const itemsPerRow = isCIM ? CIM_ITEMS_PER_ROW : ITEMS_PER_ROW;
  const rows: LibraryRow[] = [];

  rows.push({ type: "library-header", libraryId: library.id, text: library.displayName, isExpanded, isPreview });

  if (!isExpanded) return rows;

  if (isError) {
    rows.push({ type: "error", libraryId: library.id });
    return rows;
  }

  if (isPreview) {
    for (let index = 0; index < library.previewItems.length; index += itemsPerRow) {
      rows.push({
        type: "items-row",
        libraryId: library.id,
        rowIndex: index,
        items: library.previewItems.slice(index, index + itemsPerRow),
        isPreview,
      });
    }

    const count = itemsInLibrary - library.previewItems.length;
    rows.push({
      type: "preview-footer",
      libraryId: library.id,
      count,
    });

    return rows;
  }

  let isFirstGroupTitle = true;
  for (const group of library.groups) {
    if (library.groups.length > 1) {
      rows.push({
        type: isFirstGroupTitle ? "first-group-title" : "group-title",
        libraryId: library.id,
        groupId: group.id,
        text: group.displayName,
        isFirstGroupTitle,
      });
      if (isFirstGroupTitle) {
        isFirstGroupTitle = false;
      }
    }

    for (let index = 0; index < group.items.length; index += itemsPerRow) {
      rows.push({
        type: "items-row",
        libraryId: library.id,
        groupId: group.id,
        rowIndex: index,
        items: group.items.slice(index, index + itemsPerRow),
        isPreview,
      });
    }
  }

  return rows;
}

function useVirtualizedShapeLibraries(
  libraries: ShapeLibraries | null,
  filter: string,
  isFreePlan: boolean
): LibraryRow[] {
  const { isLibraryExpanded, isLibraryError } = useLibraryStates();
  const isCIM = useIsCIMReorderEnabled();

  const filteredLibraries: ShapeLibraries | null = useMemo(() => {
    if (!libraries) return null;
    if (!filter) return libraries;
    return Object.fromEntries(
      Object.entries(libraries || {})
        .map(([key, library]) => [key, filterLibrary(library, filter)])
        .filter(([_, library]) => library !== null)
    );
  }, [libraries, filter]);

  let rows: LibraryRow[] = [];
  for (const library of Object.values(filteredLibraries || {})) {
    const isPreview = isFreePlan && !FREE_LIBRARIES.has(library.id);
    let itemsInLibrary = 0;
    if (isPreview && libraries) {
      itemsInLibrary = libraries[library.id].groups.reduce((acc, group) => acc + group.items.length, 0);
    }
    rows = [
      ...rows,
      ...flattenLibrary(
        library,
        isLibraryExpanded(library.id),
        isLibraryError(library.id),
        isPreview,
        itemsInLibrary,
        isCIM
      ),
    ];
  }

  return rows;
}

export default useVirtualizedShapeLibraries;
